import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getUser,
  listAdminDeletedFiles,
  getAdminDeletedFileDownloadUrl,
  adminTrueDeleteFile,
  type DeletedFileRecord,
} from "../../api/admin.api";
import { VirtualFileList } from "../explorer/VirtualFileList";
import { VirtualFileGrid } from "../explorer/VirtualFileGrid";
import { PreviewModal } from "../preview/PreviewModal";
import { Spinner } from "../ui/Spinner";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import type { FileItem, SortField, SortOrder } from "@titancloud/shared";

const PAGE_SIZE = 50;

type ViewMode = "list" | "grid";

function toFileItem(f: DeletedFileRecord): FileItem {
  return {
    kind: "file",
    id: f.id,
    name: f.name,
    userId: f.userId,
    folderId: null,
    s3Key: f.s3Key,
    mimeType: f.mimeType,
    extension: f.extension ?? "",
    sizeBytes: f.sizeBytes,
    isDeleted: true,
    deletedAt: f.deletedAt,
    uploadStatus: "COMPLETE",
    createdAt: f.permanentlyDeletedAt ?? f.deletedAt ?? new Date().toISOString(),
    updatedAt: f.permanentlyDeletedAt ?? f.deletedAt ?? new Date().toISOString(),
  };
}

function sortItems(items: FileItem[], sort: SortField, order: SortOrder): FileItem[] {
  return [...items].sort((a, b) => {
    let cmp = 0;
    if (sort === "name") cmp = a.name.localeCompare(b.name);
    else if (sort === "size") cmp = Number(BigInt(a.sizeBytes) - BigInt(b.sizeBytes));
    else cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return order === "asc" ? cmp : -cmp;
  });
}

export default function AdminUserDeletedFiles() {
  const { userId } = useParams<{ userId: string }>();
  const qc = useQueryClient();

  const [sort, setSort] = useState<SortField>("date");
  const [order, setOrder] = useState<SortOrder>("desc");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [confirmFile, setConfirmFile] = useState<{ id: string; name: string } | null>(null);

  const userQuery = useQuery({
    queryKey: ["admin", "user", userId],
    queryFn: () => getUser(userId!),
    enabled: !!userId,
  });

  const filesQuery = useInfiniteQuery({
    queryKey: ["admin", "deleted-files", userId],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      listAdminDeletedFiles({ userId: userId!, page: pageParam, limit: PAGE_SIZE }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.page * lastPage.limit;
      return loaded < lastPage.total ? lastPage.page + 1 : undefined;
    },
    enabled: !!userId,
  });

  const trueDeleteMutation = useMutation({
    mutationFn: (fileId: string) => adminTrueDeleteFile(fileId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "deleted-files", userId] });
      toast.success("File erased from storage.");
    },
    onError: () => toast.error("Failed to erase file."),
  });

  const handleDownload = async (fileId: string, fileName: string) => {
    try {
      const { url } = await getAdminDeletedFileDownloadUrl(fileId);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
    } catch {
      toast.error("Failed to get download link.");
    }
  };

  const allRaw: DeletedFileRecord[] = useMemo(
    () => filesQuery.data?.pages.flatMap((p) => p.files) ?? [],
    [filesQuery.data]
  );
  const totalCount = filesQuery.data?.pages[0]?.total ?? 0;

  const allItems: FileItem[] = useMemo(
    () => sortItems(allRaw.map(toFileItem), sort, order),
    [allRaw, sort, order]
  );

  const handleSort = (field: SortField) => {
    if (sort === field) setOrder(order === "asc" ? "desc" : "asc");
    else { setSort(field); setOrder("desc"); }
  };

  const SortButton = ({ field, label }: { field: SortField; label: string }) => (
    <button
      type="button"
      onClick={() => handleSort(field)}
      className={`flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors ${
        sort === field
          ? "bg-amber-900/50 text-amber-100"
          : "text-amber-700/90 hover:text-amber-900 dark:text-amber-200/80 dark:hover:text-amber-50"
      }`}
    >
      {label}
      {sort === field && (
        <svg className="h-3 w-3" viewBox="0 0 12 12" fill="currentColor">
          {order === "asc" ? <path d="M6 2l4 6H2l4-6z" /> : <path d="M6 10L2 4h8l-4 6z" />}
        </svg>
      )}
    </button>
  );

  const isLoading = filesQuery.isLoading;
  const targetUser = userQuery.data;

  if (!userId) return <div className="p-6 text-center text-gray-400">Missing user.</div>;
  if (userQuery.isError) return <div className="p-6 text-center text-red-400">User not found.</div>;

  return (
    <div className="flex h-full flex-col border-t-4 border-red-700 bg-red-950/20">
      {/* Sticky banner */}
      <div className="sticky top-0 z-10 border-b border-red-700/40 bg-red-950/90 px-4 py-3 backdrop-blur-sm md:px-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-red-400">
              Permanently Deleted Files
            </p>
            <p className="mt-0.5 text-sm text-red-100/90">
              {targetUser ? (
                <>
                  <span className="font-medium">{targetUser.displayName}</span>
                  <span className="text-red-200/70"> · {targetUser.email}</span>
                </>
              ) : (
                <span className="text-red-200/70">Loading user…</span>
              )}
            </p>
            <p className="mt-1 text-xs text-red-300/80">
              Files purged from trash — still in S3. Right-click a file to download or erase.
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to={`/app/admin/users/${userId}/files`}
              className="inline-flex shrink-0 items-center justify-center rounded-lg border border-amber-600/50 bg-amber-950/50 px-3 py-2 text-sm font-medium text-amber-100 hover:bg-amber-900/40"
            >
              ← Files
            </Link>
            <Link
              to="/app/admin"
              className="inline-flex shrink-0 items-center justify-center rounded-lg border border-red-700/50 bg-red-950/50 px-3 py-2 text-sm font-medium text-red-100 hover:bg-red-900/40"
            >
              Back to admin
            </Link>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4 md:p-6">
        {/* Toolbar: sort + view toggle */}
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg border border-red-900/40 bg-gray-900/60 px-3 py-2">
          <span className="text-xs text-red-200/60">Sort</span>
          <div className="flex items-center gap-1 rounded-lg border border-red-900/50 bg-red-950/30 p-1">
            <SortButton field="name" label="Name" />
            <SortButton field="size" label="Size" />
            <SortButton field="date" label="Date" />
          </div>
          <div className="ml-auto flex rounded-lg border border-red-900/50 bg-red-950/30">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`p-1.5 transition-colors ${
                viewMode === "grid" ? "text-red-100" : "text-red-600/70 hover:text-red-200"
              }`}
              title="Grid view"
            >
              <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor">
                <path d="M1 2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H2a1 1 0 01-1-1V2zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H7a1 1 0 01-1-1V2zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1h-2a1 1 0 01-1-1V2zM1 7a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H2a1 1 0 01-1-1V7zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H7a1 1 0 01-1-1V7zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1h-2a1 1 0 01-1-1V7zM1 12a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H2a1 1 0 01-1-1v-2zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1H7a1 1 0 01-1-1v-2zm5 0a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 01-1 1h-2a1 1 0 01-1-1v-2z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`p-1.5 transition-colors ${
                viewMode === "list" ? "text-red-100" : "text-red-600/70 hover:text-red-200"
              }`}
              title="List view"
            >
              <svg className="h-4 w-4" viewBox="0 0 16 16" fill="currentColor">
                <path fillRule="evenodd" d="M2 4.5A.5.5 0 012.5 4h11a.5.5 0 010 1h-11a.5.5 0 01-.5-.5zm0 4a.5.5 0 01.5-.5h11a.5.5 0 010 1h-11a.5.5 0 01-.5-.5zm0 4a.5.5 0 01.5-.5h11a.5.5 0 010 1h-11a.5.5 0 01-.5-.5z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden rounded-xl border border-red-900/35 bg-gray-950/80">
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : allItems.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center text-red-200/50">
              <svg className="h-12 w-12 text-red-700/50" viewBox="0 0 48 48" fill="none">
                <path d="M12 16h24M19 16V12h10v4M20 24v8M28 24v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <rect x="8" y="16" width="32" height="24" rx="3" stroke="currentColor" strokeWidth="2" />
              </svg>
              <p className="text-sm">No permanently deleted files</p>
            </div>
          ) : viewMode === "grid" ? (
            <VirtualFileGrid
              items={allItems}
              onOpen={(fileId) => {
                const idx = allItems.findIndex((f) => f.id === fileId);
                if (idx !== -1) setPreviewIndex(idx);
              }}
              onRename={() => {}}
              onDelete={() => {}}
              readOnly
              explorerUserId={userId}
              onDownload={(fileId, fileName) => void handleDownload(fileId, fileName)}
              onAdminErase={(fileId, fileName) => setConfirmFile({ id: fileId, name: fileName })}
              hasNextPage={filesQuery.hasNextPage}
              isFetchingNextPage={filesQuery.isFetchingNextPage}
              onLoadMore={() => void filesQuery.fetchNextPage()}
            />
          ) : (
            <VirtualFileList
              items={allItems}
              onRename={() => {}}
              onDelete={() => {}}
              readOnly
              explorerUserId={userId}
              onDownload={(fileId, fileName) => void handleDownload(fileId, fileName)}
              onAdminErase={(fileId, fileName) => setConfirmFile({ id: fileId, name: fileName })}
              hasNextPage={filesQuery.hasNextPage}
              isFetchingNextPage={filesQuery.isFetchingNextPage}
              onLoadMore={() => void filesQuery.fetchNextPage()}
            />
          )}

          {!isLoading && totalCount > 0 && (
            <div className="flex-shrink-0 border-t border-red-900/30 bg-red-950/20 px-4 py-2.5 flex items-center justify-between gap-4">
              <span className="text-xs text-red-200/50">
                Showing{" "}
                <span className="text-red-100/80 font-medium">{allItems.length}</span>
                {" "}of{" "}
                <span className="text-red-100/80 font-medium">{totalCount}</span>
                {" "}file{totalCount !== 1 ? "s" : ""}
              </span>
              {filesQuery.isFetchingNextPage && (
                <span className="flex items-center gap-2 text-xs text-red-200/50">
                  <Spinner size="sm" />
                  Loading more…
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {viewMode === "grid" && previewIndex !== null && allItems.length > 0 && userId && (
        <PreviewModal
          files={allItems}
          initialIndex={previewIndex}
          onClose={() => setPreviewIndex(null)}
          explorerUserId={userId}
          hasMore={filesQuery.hasNextPage}
          isLoadingMore={filesQuery.isFetchingNextPage}
          onLoadMore={() => void filesQuery.fetchNextPage()}
        />
      )}

      <Modal
        open={!!confirmFile}
        onClose={() => setConfirmFile(null)}
        title="Erase file from storage?"
      >
        <p className="text-sm text-gray-300">
          <span className="font-medium text-white">{confirmFile?.name}</span> will be permanently
          erased from S3. This cannot be undone.
        </p>
        <div className="mt-4 flex justify-end gap-3">
          <Button variant="ghost" size="sm" onClick={() => setConfirmFile(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              if (confirmFile) trueDeleteMutation.mutate(confirmFile.id);
              setConfirmFile(null);
            }}
          >
            Erase permanently
          </Button>
        </div>
      </Modal>
    </div>
  );
}
